/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

/** Mirrors the `public.shipment_status` DB enum exactly. */
export type ShipmentStatus = 'planned' | 'active' | 'completed' | 'cancelled'

export const SHIPMENT_STATUSES: { value: ShipmentStatus; label: string }[] = [
  { value: 'planned', label: 'Planned' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
]

/** A row from GET /trackers/list. */
export interface TrackerListItem {
  id: number
  label: string
  vendor: string
  serialNumber: string
  modelNumber: string | null
  imei: string
  /** Already on an active or planned shipment — a warning, not a block. */
  hasOpenShipment: boolean
}

export interface GeoPoint {
  latitude: number
  longitude: number
}

/**
 * A shipment as returned by GET /shipments and PUT /shipments/:id. Timestamps
 * arrive as Date — apiService converts ISO strings on the way in.
 */
export interface ShipmentRecord {
  id: number
  shipmentName: string
  status: ShipmentStatus
  customerId: number
  trackerId: number | null
  trackerLabel: string | null
  originName: string | null
  originAddress: string | null
  originLocation: GeoPoint | null
  destinationName: string | null
  destinationAddress: string | null
  destinationLocation: GeoPoint | null
  expectedStartAt: Date | null
  expectedEndAt: Date | null
  actualStartAt: Date | null
  actualEndAt: Date | null
  minTemperatureCelsius: number | null
  maxTemperatureCelsius: number | null
}

/** PUT /shipments/:id body — every field optional; send only what changed. */
export interface UpdateShipmentBody {
  status?: ShipmentStatus
  expectedStartAt?: string
  expectedEndAt?: string
  actualStartAt?: string | null
  actualEndAt?: string | null
  originName?: string
  originAddress?: string | null
  originLatitude?: number
  originLongitude?: number
  destinationName?: string
  destinationAddress?: string | null
  destinationLatitude?: number
  destinationLongitude?: number
}

/**
 * Form state, kept flat so every field has its own validator and error.
 * Temperatures are held as typed text in the signed-in user's units and
 * converted to Celsius on submit; the DB columns are Celsius.
 *
 * `*Location` is the address text shown in the lookup; `*Latitude`/`*Longitude`
 * are filled only when a suggestion is picked, which is what makes the field
 * valid.
 */
export interface ShipmentFormValues {
  shipmentName: string
  customerId: number | null
  trackerId: number | null
  status: ShipmentStatus
  expectedStartAt: Date | null
  expectedEndAt: Date | null
  actualStartAt: Date | null
  actualEndAt: Date | null
  originName: string
  originAddress: string
  originLocation: string
  originLatitude: number | null
  originLongitude: number | null
  /** Address typed over after a pick, so it may no longer match the coordinates. */
  originAddressEdited: boolean
  destinationName: string
  destinationAddress: string
  destinationLocation: string
  destinationLatitude: number | null
  destinationLongitude: number | null
  destinationAddressEdited: boolean
  minTemperature: string
  maxTemperature: string
}

/** POST /shipments body. */
export interface CreateShipmentBody {
  shipmentName: string
  customerId: number
  trackerId: number | null
  status: ShipmentStatus
  expectedStartAt: string
  expectedEndAt: string
  actualStartAt: string | null
  actualEndAt: string | null
  originName: string
  originAddress: string | null
  originLatitude: number
  originLongitude: number
  destinationName: string
  destinationAddress: string | null
  destinationLatitude: number
  destinationLongitude: number
  minTemperatureCelsius: number
  maxTemperatureCelsius: number
}
